import { Box, Label, RiFileLine } from "@itandi/itandi-bb-ui";
import type React from "react";
import type { ReactNode } from "react";
import { toast } from "react-toastify";
import { DeleteMenuButton } from "~/components/common/parts/button/DeleteMenuButton";
import { UploadButton } from "~/components/common/upload/UploadButton";
import useLoader from "~/hooks/useLoader";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import type { DealUploadFile } from "~/models/DealUploadFile";
import { canDestroy } from "~/models/DealUploadFile";
import { isSuccess } from "~/utils/api/api_handler";
import Styles from "./UploadFileNavigation.module.scss";

type Props = {
	deal: Deal;
	description?: string | null;
	downloadExcel?: (() => void) | null;
	files: DealUploadFile[];
	fileType: string;
	handleCreate?: (() => Promise<NskResponse>) | null;
	handleDestroy?: ((file: DealUploadFile) => Promise<NskResponse>) | null;
	handleGetFilePath: (file: DealUploadFile) => string;
	handleMutate?: (() => Promise<void>) | null;
	handleUpload?: ((file: File) => Promise<NskResponse>) | null;
	isVendor?: boolean;
	title: string;
};

export const UploadFileNavigation: React.FC<Props> = ({
	title,
	deal,
	fileType,
	files,
	handleGetFilePath,
	description = null,
	downloadExcel = null,
	handleMutate = null,
	handleUpload = null,
	handleCreate = null,
	handleDestroy = null,
	isVendor = false
}: Props) => {
	const { Loader, showLoader, hideLoader } = useLoader();
	const onClickUpload = async (file: File): Promise<void> => {
		if (handleUpload === null) {
			return;
		}
		const result = await handleUpload(file);
		if (isSuccess(result)) {
			await handleMutate?.();
			toast.success("登録しました");
			return;
		}
		toast.error("エラーが発生しました");
	};
	const handleAcceptCreate = async (): Promise<void> => {
		showLoader();
		if (handleCreate === null) return;
		const result = await handleCreate();
		if (isSuccess(result)) {
			await handleMutate?.();
			hideLoader();
			toast.success("作成しました");
			return;
		}
		hideLoader();
		toast.error(result.message ?? "エラーが発生しました");
	};
	const renderRow = (file: DealUploadFile, index: number): ReactNode => {
		const extension = file.fileName.split(".").pop();
		const onClickDestroy = async (): Promise<void> => {
			if (handleDestroy === null) {
				return;
			}
			showLoader();
			const result = await handleDestroy(file);
			if (isSuccess(result)) {
				await handleMutate?.();
				hideLoader();
				toast.success("削除しました");
				return;
			}
			hideLoader();
			toast.error("エラーが発生しました");
		};
		return (
			<div className={Styles.FileRow} key={file.id}>
				<div>
					<a
						className={Styles.FileLink}
						href={handleGetFilePath(file)}
						rel="noreferrer"
						target="_blank"
					>
						{index + 1}_{deal.name}_{deal.roomNumber}_{fileType}.{extension}
					</a>
				</div>
				{handleDestroy !== null && canDestroy(file, isVendor) && (
					<div className={Styles.FileButtons}>
						<DeleteMenuButton onClick={onClickDestroy}>削除</DeleteMenuButton>
					</div>
				)}
			</div>
		);
	};
	return (
		<div className={Styles.NavigationBlock}>
			<Loader />
			<div className={Styles.IconWrapper}>
				<RiFileLine className={Styles.Icon} />
			</div>
			<div className={Styles.NavigationContent}>
				<div>
					<h4 className={Styles.Title}>
						{title}
						<span className={Styles.OptionalLabel}>任意</span>
					</h4>
					{description !== null && (
						<Box marginBottom={8}>
							<Label color="subtle" size="xs">
								{description}
							</Label>
						</Box>
					)}
					{files.map(renderRow)}
				</div>
			</div>
			<div className={Styles.ButtonArea}>
				{handleCreate !== null && (
					<div className={Styles.DetailLinkBlock}>
						<button
							className={Styles.CreateButton}
							onClick={handleAcceptCreate}
							type="button"
						>
							PDF作成
						</button>
					</div>
				)}
				{downloadExcel !== null && (
					<div className={Styles.DetailLinkBlock}>
						<button
							className={Styles.CreateButton}
							onClick={downloadExcel}
							type="button"
						>
							Excelダウンロード
						</button>
					</div>
				)}
				{handleUpload !== null && (
					<div className={Styles.DetailLinkBlock}>
						<UploadButton
							className={Styles.DetailLinkButton}
							onClick={onClickUpload}
							variant="normal"
						>
							ファイル登録
						</UploadButton>
					</div>
				)}
			</div>
		</div>
	);
};
